// Liveliness: pure, deterministic choices so Home and the lesson end never read the same
// two days running. No DOM, no API. Seeded by day + member id, so a reload never flickers.
// Copy rules: grade 3, sentences of 10 words or fewer, no dashes, no semicolons, warm, never shaming.

export const hashStr = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; };
export const pick = <T>(list: readonly T[], seed: string): T => list[hashStr(seed) % list.length];

export type TimeOfDay = 'morning' | 'afternoon' | 'evening';
export const timeOfDay = (hour: number): TimeOfDay => (hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening');
export const TIME_WORD: Record<TimeOfDay, string> = { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' };

export interface RingParts { learn: boolean; log: boolean; breathe: boolean }
export type RingState = 'fresh' | 'going' | 'closed';
export function ringState(r: RingParts | null | undefined): RingState {
  const n = r ? [r.learn, r.log, r.breathe].filter(Boolean).length : 0;
  return n === 0 ? 'fresh' : n === 3 ? 'closed' : 'going';
}

/** One warm line under the greeting. Three per time of day and ring state, 27 in all. */
export const GREETINGS: Record<TimeOfDay, Record<RingState, readonly string[]>> = {
  morning: {
    fresh: ['A new day. One small step is plenty.', 'Morning light, fresh start. What feels good first?', 'Your day is wide open. Start soft.'],
    going: ['Already moving this morning. Lovely.', 'One step in before noon. That counts.', 'You started early. Keep it gentle.'],
    closed: ['Ring closed before lunch. Look at you.', 'All three done and the day is young.', 'Morning done and dusted. Enjoy the rest.'],
  },
  afternoon: {
    fresh: ['Good afternoon. Small steps still count now.', 'A quiet moment? Let us make it count.', 'The day is half here. Pick one thing.'],
    going: ['You are on your way today. Nice.', 'One or two done. The rest is easy.', 'Midday and moving. Keep going, gently.'],
    closed: ['Ring closed. The afternoon is yours.', 'Three small things, all done. Well done.', 'You did it all. Rest a little.'],
  },
  evening: {
    fresh: ['Evening is a fine time to start.', 'A soft end to the day starts here.', 'Still time for one small thing.'],
    going: ['You showed up today. That matters.', 'Good progress today. Finish soft or stop here.', 'The day gave you something. Nice work.'],
    closed: ['Ring closed. Sleep well tonight.', 'A full day, done with care. Rest now.', 'All three done. Tonight, rest easy.'],
  },
};
export const greetingLine = (o: { day: string; memberId: string; hour: number; ring: RingParts | null | undefined }) =>
  pick(GREETINGS[timeOfDay(o.hour)][ringState(o.ring)], `${o.day}:${o.memberId}:greet`);

/** Which card leads Home today: one she has not done yet, chosen by the day. */
export type LeadKind = 'lesson' | 'practice' | 'challenge' | 'review' | 'log' | 'followup';
export interface LeadInput { lessonOpen: boolean; learnDone: boolean; practiceOpen: boolean; practiceDone: boolean; challengeOpen: boolean; challengeDone: boolean; reviewReady: boolean; loggedToday: boolean; followUp?: boolean }
export function leadCandidates(i: LeadInput): LeadKind[] {
  const out: LeadKind[] = [];
  if (i.followUp) out.push('followup');
  if (i.lessonOpen && !i.learnDone) out.push('lesson');
  if (i.practiceOpen && !i.practiceDone) out.push('practice');
  if (i.challengeOpen && !i.challengeDone) out.push('challenge');
  if (i.reviewReady) out.push('review');
  if (!i.loggedToday) out.push('log');
  return out;
}
/** Yesterday's action, when one is waiting, always leads: it is first in the list and skips the day's draw. */
export const pickLead = (c: LeadKind[], day: string, memberId: string): LeadKind | null => (c[0] === 'followup' ? 'followup' : c.length ? pick(c, `${day}:${memberId}:lead`) : null);
export const LEAD_EYEBROW = 'Start here';

/** Yesterday, as the dashboard reports it (api.ts GET /api/dashboard `yesterday`). */
export interface Yesterday { logged: boolean; ringClosed: boolean; lessonTitle: string | null; practice: boolean; challengeDay: number | null; breathMinutes: number }
export const SMALL_WINS = {
  ring: 'Yesterday you closed your ring. That is how it grows.',
  lesson: 'Yesterday you finished {title}. It is still with you.',
  practice: 'Yesterday you did your practice. Your body noticed.',
  challenge: 'Yesterday was day {n} of your challenge. Nice.',
  breath: 'Yesterday you breathed for {n} minutes. Calm adds up.',
  breath1: 'Yesterday you breathed for a minute. Calm adds up.',
  logged: 'You logged yesterday. Marie reads every line.',
} as const;
export function smallWin(y: Yesterday | null | undefined): string | null {
  if (!y) return null;
  if (y.ringClosed) return SMALL_WINS.ring;
  if (y.lessonTitle) return SMALL_WINS.lesson.replace('{title}', y.lessonTitle);
  if (y.practice) return SMALL_WINS.practice;
  if (y.challengeDay) return SMALL_WINS.challenge.replace('{n}', String(y.challengeDay));
  if (y.breathMinutes >= 2) return SMALL_WINS.breath.replace('{n}', String(y.breathMinutes));
  if (y.breathMinutes === 1) return SMALL_WINS.breath1;
  if (y.logged) return SMALL_WINS.logged;
  return null;
}

export interface LastLesson { id: string; title: string; line: string }
export type SideLine = { kind: 'win'; text: string } | { kind: 'know'; text: string; lesson: { id: string; title: string } };
export const KNOW_EYEBROW = 'From your last lesson';
/** The one line under the ring: a small win from yesterday, or every third day the first line of her last lesson. */
export function sideLine(o: { day: string; memberId: string; yesterday: Yesterday | null | undefined; lastLesson: LastLesson | null | undefined }): SideLine | null {
  const win = smallWin(o.yesterday);
  const know: SideLine | null = o.lastLesson?.line ? { kind: 'know', text: o.lastLesson.line, lesson: { id: o.lastLesson.id, title: o.lastLesson.title } } : null;
  const knowDay = hashStr(`${o.day}:${o.memberId}:side`) % 3 === 0;
  if (knowDay && know) return know;
  return win ? { kind: 'win', text: win } : know;
}

/** The lesson end headline. A milestone title always wins over these. */
export const COMPLETE_HEADLINES = ['You did it', 'One more step, done', 'That one is yours now', 'Beautifully done', 'Learned and lived', 'Another lesson in your pocket', 'Nicely done, {name}', 'Small step, big roots'] as const;
export const completeHeadline = (lessonId: string, memberId: string, firstName: string) =>
  pick(COMPLETE_HEADLINES, `${lessonId}:${memberId}:done`).replace('{name}', firstName || 'friend');

/** How the breathing ball moves for a guided cue: grows on an in breath, shrinks on an out breath, cycles 4 in 6 out when the cue covers several breaths. */
export function cueMode(say: string): 'in' | 'out' | 'cycle' | 'none' {
  const s = say.toLowerCase();
  const inn = /\bbreathe in\b|\bbreath in\b|\bin through\b|\bin, softly\b/.test(s);
  const out = /\bbreathe out\b|\bbreath out\b|\bout slowly\b|\bout, let\b|\blong, slow breath\b/.test(s);
  if (/\bmore times\b|\beach breath\b|\bkeep each\b|\bcome back to your breath\b|\blast few breaths\b|\bas you breathe\b/.test(s) || (inn && out)) return 'cycle';
  if (inn) return 'in';
  if (out) return 'out';
  return 'none';
}

/** SIMPLE_HOME=1: the ring week as the calendar week, Sunday to Saturday, letters S M T W T F S. `week` is the ring's last seven days (api.ts ring()), which always holds every day of this calendar week up to today; days after today are open. */
export const WEEK_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
export function calendarWeek(week: { date: string; closed: boolean }[], today: string): { day: string; closed: boolean; isToday: boolean }[] {
  const [y, m, d] = today.split('-').map(Number);
  const base = Date.UTC(y, m - 1, d), dow = new Date(base).getUTCDay();
  return WEEK_LETTERS.map((day, i) => {
    const date = new Date(base + (i - dow) * 86400000).toISOString().slice(0, 10);
    return { day, closed: i <= dow && !!week.find((w) => w.date === date)?.closed, isToday: i === dow };
  });
}
